import { Supplier, categoryConfig, statusConfig } from '../../../mocks/suppliers';

interface SupplierCardProps {
  supplier: Supplier;
  onView: (supplier: Supplier) => void;
  onEdit: (supplier: Supplier) => void;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`text-xs ${s <= Math.round(rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-200'}`}
        />
      ))}
    </div>
  );
}

export default function SupplierCard({ supplier, onView, onEdit }: SupplierCardProps) {
  const cat = categoryConfig[supplier.category];
  const st = statusConfig[supplier.status];
  const margin = supplier.totalSellValue > 0
    ? Math.round(((supplier.totalSellValue - supplier.totalCostValue) / supplier.totalSellValue) * 100)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all flex flex-col overflow-hidden">
      {/* Top accent line */}
      <div className="h-1 w-full" style={{ backgroundColor: supplier.logoColor }} />

      <div className="p-5 flex flex-col gap-4 flex-1">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ backgroundColor: supplier.logoColor }}
          >
            {supplier.logo}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm font-bold text-gray-800 leading-snug">{supplier.name}</h3>
              <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${st.bg} ${st.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                {supplier.status}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.bg} ${cat.color}`}>
                <i className={`${cat.icon} mr-1`} />
                {supplier.category}
              </span>
            </div>
          </div>
        </div>

        {/* Location & Rating */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <i className="ri-map-pin-line text-gray-300" />
            <span>{supplier.city}, {supplier.country}</span>
          </div>
          <div className="flex items-center gap-2">
            <StarRating rating={supplier.rating} />
            <span className="text-xs font-semibold text-gray-600">{supplier.rating}</span>
            <span className="text-xs text-gray-400">({supplier.reviewCount})</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{supplier.description}</p>

        {/* Primary Contact */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
          <p className="text-xs font-semibold text-gray-400">Primary Contact</p>
          <p className="text-sm font-semibold text-gray-700">{supplier.contacts[0].name}</p>
          <p className="text-xs text-gray-500">{supplier.contacts[0].role}</p>
          <div className="flex items-center gap-3 pt-0.5">
            <a href={`mailto:${supplier.contacts[0].email}`} className="text-xs text-amber-600 hover:underline flex items-center gap-1 cursor-pointer">
              <i className="ri-mail-line" /> {supplier.contacts[0].email}
            </a>
          </div>
          <p className="text-xs text-gray-500">{supplier.contacts[0].phone}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-base font-bold text-gray-800">{supplier.totalBookings}</p>
            <p className="text-xs text-gray-400 mt-0.5">Bookings</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-sm font-bold text-gray-800">
              AED {(supplier.totalSellValue / 1000).toFixed(0)}k
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Revenue</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-base font-bold text-emerald-600">{margin}%</p>
            <p className="text-xs text-gray-400 mt-0.5">Margin</p>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {supplier.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{tag}</span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-1.5 pt-1 border-t border-gray-50">
          <p className="text-xs text-gray-400 flex-1">
            Last used: {new Date(supplier.lastUsed).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
          </p>
          <button
            onClick={() => onEdit(supplier)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <i className="ri-edit-line text-sm" />
          </button>
          <button
            onClick={() => onView(supplier)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line text-sm" /> View Profile
          </button>
        </div>
      </div>
    </div>
  );
}
